import { Activity, CheckCircle2, HelpCircle, Link2, Mail, RefreshCw, Share2, Watch, XCircle } from 'lucide-react'

interface Step {
  num: number
  title: string
  body: string
  screenshot?: { src: string; alt: string }
}

interface Section {
  icon: typeof Activity
  title: string
  intro?: string
  steps: Step[]
}

const SECTIONS: Section[] = [
  {
    icon: Link2,
    title: '1. Connect your Strava account',
    intro: 'You can connect Strava from inside Parlent Fit in less than a minute. Your Strava credentials are never seen by Parlent Fit — you sign in directly with Strava.',
    steps: [
      {
        num: 1,
        title: 'Open Parlent Fit and go to Profile',
        body: 'Launch the Parlent Fit app on your phone, then tap the profile icon at the bottom-right of the navigation bar.',
        screenshot: { src: '/strava-support/01-profile.png', alt: 'Profile tab in Parlent Fit' },
      },
      {
        num: 2,
        title: 'Tap "Integrations" → "Strava"',
        body: 'In the profile screen, scroll to the Integrations section and tap the Strava row.',
        screenshot: { src: '/strava-support/02-integrations.png', alt: 'Integrations list with Strava option' },
      },
      {
        num: 3,
        title: 'Tap "Connect with Strava"',
        body: 'You will see the official orange "Connect with Strava" button. Tap it to open the Strava authorisation page in your browser.',
        screenshot: { src: '/strava-support/03-connect-button.png', alt: 'Connect with Strava button' },
      },
      {
        num: 4,
        title: 'Authorise Parlent Fit on Strava',
        body: 'Sign in to Strava (if not already), then review the permissions Parlent Fit requests (write activities). Tap "Authorize" to continue.',
        screenshot: { src: '/strava-support/04-strava-authorize.png', alt: 'Strava authorisation page' },
      },
      {
        num: 5,
        title: 'You’re connected!',
        body: 'The browser will redirect back to Parlent Fit automatically. You will now see "Connected to Strava" on the Integrations screen.',
        screenshot: { src: '/strava-support/05-connected.png', alt: 'Connected to Strava confirmation' },
      },
    ],
  },
  {
    icon: Share2,
    title: '2. Share a workout to Strava',
    intro: 'After recording a workout on your Parlent smartwatch and syncing to the phone, you can push it to Strava with a single tap.',
    steps: [
      {
        num: 1,
        title: 'Open the workout from history',
        body: 'In Parlent Fit, go to Workout History and tap the session you want to share.',
        screenshot: { src: '/strava-support/06-workout-history.png', alt: 'Workout history list' },
      },
      {
        num: 2,
        title: 'Tap "Share to Strava"',
        body: 'On the workout detail screen, tap the orange "Share to Strava" button. Parlent Fit will upload the activity (name, sport type, duration, distance, optional GPS route) to your Strava account.',
        screenshot: { src: '/strava-support/07-share-button.png', alt: 'Share to Strava button on workout detail' },
      },
      {
        num: 3,
        title: 'Upload confirmation',
        body: 'You will see an "Upload successful" toast. The activity now appears in your Strava feed with the description "Tracked with Parlent Fit".',
        screenshot: { src: '/strava-support/08-upload-success.png', alt: 'Upload success toast' },
      },
    ],
  },
  {
    icon: RefreshCw,
    title: '3. Enable automatic sync',
    intro: 'If you don’t want to share each workout manually, you can enable automatic Strava sync. Every completed workout will then be sent to Strava immediately.',
    steps: [
      {
        num: 1,
        title: 'Open Integrations → Strava',
        body: 'Go back to Profile → Integrations → Strava (same path as connecting).',
      },
      {
        num: 2,
        title: 'Toggle "Auto-sync workouts"',
        body: 'Enable the "Auto-sync workouts" switch. From now on, every workout that finishes on your Parlent watch is automatically pushed to Strava.',
        screenshot: { src: '/strava-support/09-auto-sync.png', alt: 'Auto-sync toggle' },
      },
    ],
  },
  {
    icon: XCircle,
    title: '4. Disconnect Strava',
    intro: 'You can disconnect Parlent Fit from Strava at any time. Existing activities already uploaded will remain in your Strava feed.',
    steps: [
      {
        num: 1,
        title: 'In Parlent Fit',
        body: 'Profile → Integrations → Strava → tap "Disconnect". Confirm the prompt. Parlent Fit will delete the Strava tokens from your device.',
      },
      {
        num: 2,
        title: 'On Strava (alternative)',
        body: 'You can also revoke access directly from your Strava account at any time:',
      },
      {
        num: 3,
        title: 'Visit strava.com/settings/apps',
        body: 'Go to https://www.strava.com/settings/apps, find "Parlent Fit" in the list, and tap "Revoke Access".',
      },
    ],
  },
  {
    icon: HelpCircle,
    title: '5. Troubleshooting',
    steps: [
      {
        num: 1,
        title: 'I tapped "Connect" but nothing happens',
        body: 'Ensure your phone has an internet connection and a default browser installed. If the deep-link redirect fails, try again from the same screen — the OAuth state is only valid for 60 seconds.',
      },
      {
        num: 2,
        title: 'I get "Upload failed" when sharing a workout',
        body: 'Confirm you are connected to Strava (Profile → Integrations → Strava shows "Connected"). If your access token expired, Parlent Fit refreshes it automatically — try once more. If the failure persists, disconnect and reconnect Strava.',
      },
      {
        num: 3,
        title: 'My GPS route is missing on Strava',
        body: 'GPX routes are only sent for outdoor activities recorded with GPS enabled on the watch. Indoor activities (treadmill, gym, yoga) are uploaded as manual activities without a route.',
      },
      {
        num: 4,
        title: 'Strava shows the wrong sport type',
        body: 'Parlent Fit maps watch sport modes to Strava sport types. If a mapping is incorrect, please email us with the watch sport name and the Strava sport you expected.',
      },
      {
        num: 5,
        title: 'I want to delete data already uploaded to Strava',
        body: 'Once an activity is on Strava, it lives in your Strava account. You can delete or hide it from your Strava feed at any time using the Strava mobile or web app.',
      },
    ],
  },
]

export function StravaSupportPublicPage() {
  return (
    <div className="min-h-[100dvh] h-full bg-[#0a0a0a] text-stone-300">
      <div className="max-w-3xl mx-auto bg-[#141414] min-h-[100dvh] border-x border-stone-800 shadow-2xl">
        <header className="sticky top-0 z-10 bg-[#141414]/90 backdrop-blur-md border-b border-stone-800 p-4 sm:p-6 flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-rose-500 flex items-center justify-center shrink-0 overflow-hidden outline outline-1 outline-white/10 outline-offset-[-1px]">
            <img src="/adaptive_foreground_icon.jpeg" alt="Parlent World logo" className="w-full h-full object-cover" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white tracking-tight">Parlent Fit · Strava Support</h1>
            <p className="text-xs text-stone-500 font-medium uppercase tracking-wider">Connect &amp; share workouts</p>
          </div>
        </header>

        <main className="p-6 sm:p-8 space-y-12">
          <section className="space-y-4">
            <div className="flex items-center gap-3">
              <Watch className="h-6 w-6 text-orange-500" />
              <h2 className="text-2xl font-bold text-white">Welcome, athlete</h2>
            </div>
            <p className="text-[15px] leading-7 text-stone-400">
              Parlent Fit lets you record workouts on your Parlent smartwatch and share them to Strava with one tap.
              This guide walks through connecting, sharing, and managing your Strava integration. If you run into trouble,
              we&rsquo;re only an email away.
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              <a
                href="mailto:parlent.dev@gmail.com"
                className="flex items-center gap-3 rounded-2xl border border-stone-800 bg-stone-900/50 px-4 py-3 hover:border-orange-500/40 hover:bg-orange-500/5 transition"
              >
                <Mail className="h-5 w-5 text-orange-500 shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-white">Email support</p>
                  <p className="text-xs text-stone-500 truncate">parlent.dev@gmail.com</p>
                </div>
              </a>
              <a
                href="https://www.strava.com/settings/apps"
                target="_blank"
                rel="noreferrer noopener"
                className="flex items-center gap-3 rounded-2xl border border-stone-800 bg-stone-900/50 px-4 py-3 hover:border-orange-500/40 hover:bg-orange-500/5 transition"
              >
                <Activity className="h-5 w-5 text-orange-500 shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-white">Manage on Strava</p>
                  <p className="text-xs text-stone-500 truncate">strava.com/settings/apps</p>
                </div>
              </a>
            </div>
          </section>

          {SECTIONS.map((section) => {
            const Icon = section.icon
            return (
              <section key={section.title} className="space-y-5">
                <div className="flex items-center gap-3">
                  <Icon className="h-6 w-6 text-orange-500" />
                  <h2 className="text-2xl font-bold text-white">{section.title}</h2>
                </div>
                {section.intro && (
                  <p className="text-[15px] leading-7 text-stone-400">{section.intro}</p>
                )}
                <ol className="space-y-6">
                  {section.steps.map((step) => (
                    <li key={step.num} className="flex gap-4">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-orange-500/10 border border-orange-500/30 text-sm font-bold text-orange-400">
                        {step.num}
                      </div>
                      <div className="flex-1 space-y-3 min-w-0">
                        <h3 className="text-base font-semibold text-white">{step.title}</h3>
                        <p className="text-[15px] leading-7 text-stone-400">{step.body}</p>
                        {step.screenshot && (
                          <div className="rounded-2xl border border-stone-800 bg-stone-900/30 overflow-hidden">
                            <img
                              src={step.screenshot.src}
                              alt={step.screenshot.alt}
                              loading="lazy"
                              className="w-full max-h-[480px] object-contain bg-stone-950"
                              onError={(e) => {
                                ;(e.target as HTMLImageElement).style.display = 'none'
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ol>
              </section>
            )
          })}

          <section className="rounded-2xl border border-orange-500/30 bg-orange-500/5 p-5 space-y-3">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-5 w-5 text-orange-400" />
              <h2 className="text-base font-bold text-white">Privacy &amp; data</h2>
            </div>
            <p className="text-sm leading-6 text-stone-400">
              Parlent Fit never stores your Strava credentials. OAuth tokens issued by Strava are saved only on your device
              using iOS Keychain / Android Keystore. We never transmit Strava data to our servers. Read the full
              {' '}<a href="/privacy-policy" className="text-orange-400 hover:text-orange-300">Privacy Policy</a> and
              {' '}<a href="/terms-of-service" className="text-orange-400 hover:text-orange-300">Terms of Service</a>.
            </p>
          </section>
        </main>

        <footer className="p-6 border-t border-stone-800 text-center bg-stone-900/30">
          <p className="text-xs text-stone-500">© 2026 Parlent World. All rights reserved.</p>
        </footer>
      </div>
    </div>
  )
}
